import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  RefreshCcw, Mail, MailOpen, Trash2, User, Paperclip, Reply, Send,
  CalendarPlus, ClipboardCheck, ArrowLeft, Inbox, CheckCircle2, Sparkles,
  ShieldBan, Search, MessagesSquare, BellRing, FileText, MoreHorizontal, X, Copy,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { parseContactFormEmail, CONTACT_FIELD_LABELS } from "@/lib/parseContactFormEmail";

const fmt = (iso) => new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
const isContactRequest = (subject = "") => /contact form|neuer eintrag|new entry|anfrage/i.test(subject);
const messageBodyForParser = (message = {}) => message.html
  || `<html><body>${(message.text || "").replace(/\n/g, "<br>")}</body></html>`;

const copyEmailText = async (message = {}) => {
  let text = "";

  if (message.text) {
    text = splitQuotedMail(message.text).current.trim();
  } else if (message.html) {
    const doc = new DOMParser().parseFromString(message.html, "text/html");
    text = (doc.body?.innerText || doc.body?.textContent || "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }

  if (!text) {
    toast.error("Kein Text zum Kopieren");
    return;
  }

  try {
    await navigator.clipboard.writeText(text);
    toast.success("Text kopiert");
  } catch {
    toast.error("Text konnte nicht kopiert werden");
  }
};

const renderTextWithLinks = (text = "") => {
  const urlPattern = /(https?:\/\/[^\s<>]+)/gi;
  return text.split(urlPattern).map((part, index) => {
    if (!/^https?:\/\//i.test(part)) return part;
    const trailing = part.match(/[),.;!?]+$/)?.[0] || "";
    const href = trailing ? part.slice(0, -trailing.length) : part;
    return (
      <span key={`${href}-${index}`}>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="break-all font-medium text-blue-600 underline decoration-blue-300 underline-offset-2 hover:text-blue-800"
        >
          {href}
        </a>
        {trailing}
      </span>
    );
  });
};

const splitQuotedMail = (text = "") => {
  if (!text) return { current: "", quoted: "" };

  const normalized = text.replace(/\r\n/g, "\n");

  // Formate uzuale generate automat de clienti de e-mail.
  // Nu taiem doar dupa un cuvant izolat: pentru Original-Nachricht
  // cerem si antete tipice precum Von/Betreff/Datum/An.
  const patterns = [
    /\n\s*-{2,}\s*Original-Nachricht\s*-{2,}\s*\n(?=(?:>\s*)?Von:\s*.+\n(?:>\s*)?Betreff:\s*.+\n(?:>\s*)?Datum:\s*.+\n(?:>\s*)?An:\s*.+)/i,
    /\n\s*-{2,}\s*Original Message\s*-{2,}\s*\n(?=(?:>\s*)?From:\s*.+\n(?:>\s*)?Subject:\s*.+\n(?:>\s*)?Date:\s*.+\n(?:>\s*)?To:\s*.+)/i,
    /\n\s*-{3,}\s*\n(?=Am .+ schrieb(?:\s+.+)?:)/i,
    /\n(?=Am .+ schrieb(?:\s+.+)?:)/i,
    /\n(?=On .+ wrote:)/i,
    /\n(?=On [^\n]+(?:\n[^\n]+){0,2}\s+wrote:)/i,
  ];

  let firstMatch = null;

  for (const pattern of patterns) {
    const match = normalized.match(pattern);
    if (match && match.index > 0) {
      if (!firstMatch || match.index < firstMatch.index) {
        firstMatch = match;
      }
    }
  }

  if (!firstMatch) {
    return { current: normalized.trim(), quoted: "" };
  }

  const current = normalized.slice(0, firstMatch.index).trim();
  const quoted = normalized
    .slice(firstMatch.index + firstMatch[0].length)
    .trim()
    .split("\n")
    .map((line) => line.replace(/^\s*(?:>\s*)+/, ""))
    .join("\n")
    .replace(/\n[ \t]*\n(?:[ \t]*\n)+/g, "\n\n");

  return { current, quoted };
};


const contactFormCandidate = (message = {}) => {
  const parsed = parseContactFormEmail(messageBodyForParser(message));
  const body = `${message.subject || ""} ${message.text || ""} ${message.html || ""}`;
  const formMarkers = [
    /wpforms/i, /fahrzeugmarke/i, /fahrzeugmodell|\bmodell\b/i,
    /kennzeichen/i, /beschreiben sie kurz ihr anliegen/i,
  ].filter((pattern) => pattern.test(body)).length;
  const coreFields = ["name", "email", "phone", "car_model", "vehicle_brand", "vehicle_model", "service_name"]
    .filter((key) => parsed[key]).length;
  const score = (parsed.recognized?.length || 0) * 10
    + coreFields * 8
    + formMarkers * 6
    + (isContactRequest(message.subject) ? 12 : 0);
  return { message, parsed, score, formMarkers, coreFields };
};

const trustedContactFormData = (message = {}) => {
  const candidate = contactFormCandidate(message);
  return candidate.formMarkers > 0 && candidate.coreFields >= 2
    ? candidate.parsed
    : null;
};

export default function Emails() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [folder, setFolder] = useState("inbox");
  const [selectedId, setSelectedId] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [compose, setCompose] = useState({ to: "", subject: "", body: "", reply_to_id: null });
  const [composeFiles, setComposeFiles] = useState([]);
  const [search, setSearch] = useState("");
  const attachmentInputRef = useRef(null);

  const importAsAppointment = (message, terminart) => {
    const currentTime = new Date(message.date || 0).getTime();
    const allCandidates = conversationItems
      .filter((item) => item.direction !== "sent")
      .map(contactFormCandidate)
      .filter((item) => item.formMarkers > 0 && item.coreFields >= 2);
    const precedingCandidates = allCandidates
      .filter((item) => {
        const candidateTime = new Date(item.message.date || 0).getTime();
        return !currentTime || !candidateTime || candidateTime <= currentTime;
      });
    const candidates = (precedingCandidates.length > 0 ? precedingCandidates : allCandidates)
      .sort((a, b) => {
        const dateDifference = new Date(b.message.date || 0).getTime() - new Date(a.message.date || 0).getTime();
        return dateDifference || b.score - a.score;
      });
    const source = candidates[0]?.message || message;
    const parsed = candidates[0]?.parsed || parseContactFormEmail(messageBodyForParser(message));
    const params = new URLSearchParams({
      new: "1",
      terminart,
      name: parsed.name || source.from_name || message.from_name || message.from_email.split("@")[0],
      email: parsed.email || source.from_email || message.from_email,
    });
    ["phone", "car_model", "vehicle_brand", "vehicle_model", "plate", "service_name"].forEach((key) => {
      if (parsed[key]) params.set(key, parsed[key]);
    });
    if (parsed.color) params.set("vehicle_color", parsed.color);
    if (parsed.year) params.set("vehicle_year", parsed.year);

    const notesParts = [];
    if (parsed.notes) notesParts.push(parsed.notes);
    if (parsed.year) notesParts.push(`Baujahr: ${parsed.year}`);
    notesParts.push(`— Aus E-Mail: ${source.subject || message.subject}`);
    params.set("notes", notesParts.join("\n\n"));

    if (parsed.recognized.length === 0) {
      toast.warning("Keine Formularfelder erkannt. Absender und Nachricht wurden übernommen.");
    } else if (parsed.missing.length > 0) {
      const missingLabels = parsed.missing.map((key) => CONTACT_FIELD_LABELS[key]).join(", ");
      toast.warning(`Übernommen. Nicht erkannt: ${missingLabels}`, { duration: 6000 });
    } else {
      toast.success(
        terminart === "begutachtung"
          ? `Als Begutachtung übernommen (${parsed.recognized.length} Felder)`
          : `Als Termin übernommen (${parsed.recognized.length} Felder)`
      );
    }
    navigate(`/kalender?${params.toString()}`);
  };

  const openReply = (m) => {
    const parsed = trustedContactFormData(m) || {};
    const recipient = parsed.email || m.from_email;
    const senderLabel = parsed.name || m.from_name || m.from_email;
    const isGenericContactSubject = /^(neuer eintrag|new entry|contact form)/i.test((m.subject || "").trim());
    const smartSubject = isGenericContactSubject
      ? (parsed.service_name
          ? `Ihre Anfrage – ${parsed.service_name}`
          : "Ihre Anfrage bei Auto Stellar Aufbereitung")
      : (m.subject.startsWith("Re:") ? m.subject : `Re: ${m.subject}`);
    const quote = (m.text || "").split("\n").join("\n> ");
    setCompose({
      to: recipient,
      subject: smartSubject,
      body:
`

---
Am ${fmt(m.date)} schrieb ${senderLabel}:
> ${quote}`,
      reply_to_id: m.id,
    });
    setComposeFiles([]);
    setComposeOpen(true);
  };

  const addComposeFiles = (fileList) => {
    const incoming = Array.from(fileList || []).filter((file) => file && file.size >= 0);
    if (incoming.length === 0) return;
    const next = [...composeFiles, ...incoming];
    const totalSize = next.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > 20 * 1024 * 1024) {
      toast.error("Anhänge dürfen zusammen höchstens 20 MB groß sein");
      return;
    }
    setComposeFiles(next);
  };

  const sendMut = useMutation({
    mutationFn: async () => {
      const data = new FormData();
      data.append("to", JSON.stringify(compose.to.split(",").map((s) => s.trim()).filter(Boolean)));
      data.append("subject", compose.subject);
      data.append("body", compose.body);
      if (compose.reply_to_id) data.append("reply_to_id", compose.reply_to_id);
      composeFiles.forEach((file) => data.append("files", file));
      return (await api.post("/email/send", data)).data;
    },
    onSuccess: () => {
      toast.success("E-Mail gesendet");
      setComposeOpen(false);
      setComposeFiles([]);
      qc.invalidateQueries({ queryKey: ["emails-sent"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const { data: cfg } = useQuery({
    queryKey: ["email-config"],
    queryFn: async () => (await api.get("/email/config")).data,
  });

  const { data: inboxMessages = [] } = useQuery({
    queryKey: ["emails", search],
    queryFn: async () => (await api.get("/email/messages", { params: { q: search.trim() || undefined } })).data,
  });

  const { data: sentMessages = [] } = useQuery({
    queryKey: ["emails-sent", search],
    queryFn: async () => (await api.get("/email/sent", { params: { q: search.trim() || undefined } })).data,
  });

  const messages = folder === "inbox" ? inboxMessages : sentMessages;
  const unreadCount = inboxMessages.filter((m) => !m.read).length;
  const repliedIds = new Set(sentMessages.map((m) => m.reply_to_id).filter(Boolean));

  const { data: current } = useQuery({
    queryKey: ["email", folder, selectedId],
    queryFn: async () => (await api.get(
      folder === "inbox" ? `/email/messages/${selectedId}` : `/email/sent/${selectedId}`
    )).data,
    enabled: !!selectedId,
  });
  const parsedCurrent = current && folder === "inbox"
    ? trustedContactFormData(current)
    : null;
  const currentAddress = current
    ? (folder === "inbox" ? (parsedCurrent?.email || current.from_email) : (current.to || [])[0])
    : "";
  const isOwnAddress = !!currentAddress && !!cfg?.user
    && currentAddress.toLowerCase() === cfg.user.toLowerCase();
  const { data: conversation } = useQuery({
    queryKey: ["email-conversation", currentAddress],
    queryFn: async () => (await api.get("/email/conversation", { params: { address: currentAddress } })).data,
    enabled: !!currentAddress && !isOwnAddress,
  });
  const currentThreadItem = current
    ? { ...current, direction: folder === "inbox" ? "inbox" : "sent" }
    : null;
  // The explicitly opened message must always be first and must come from its
  // own detail endpoint. Conversation data is supplementary and may never
  // replace the selected message (especially for website contact forms).
  const conversationItems = [
    ...(conversation?.items || []).filter((item) => item.id !== selectedId),
    ...(currentThreadItem ? [currentThreadItem] : []),
  ].sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));

  useEffect(() => {
    if (!selectedId || !current) return;

    const timer = window.setTimeout(() => {
      const element = document.getElementById(`email-thread-${selectedId}`);
      element?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);

    return () => window.clearTimeout(timer);
  }, [selectedId, current, conversation?.items]);

  const syncMut = useMutation({
    mutationFn: async () => (await api.post("/email/sync")).data,
    onSuccess: (d) => {
      toast.success(`${d.new} neue E-Mails · ${d.database_total} im ERP · ${d.server_total} im Posteingang`);
      qc.invalidateQueries({ queryKey: ["emails"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(
      folder === "inbox" ? `/email/messages/${id}` : `/email/sent/${id}`
    )).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: [folder === "inbox" ? "emails" : "emails-sent"] });
      setSelectedId(null);
      if (folder === "inbox" && data?.server_action === "failed") {
        toast.warning("Aus ERP entfernt. Der IMAP-Papierkorb war nicht erreichbar.");
      } else {
        toast.success(folder === "inbox" ? "In den Papierkorb verschoben" : "Gelöscht");
      }
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const spamMut = useMutation({
    mutationFn: async (id) => (await api.post(`/email/messages/${id}/spam`)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["emails"] });
      qc.invalidateQueries({ queryKey: ["email-blocked-senders"] });
      setSelectedId(null);
      if (data?.server_action === "failed" || data?.server_action === "not_moved") {
        toast.warning("Absender blockiert. Der Spam-Ordner war nicht erreichbar.");
      } else {
        toast.success("Als Spam markiert und Absender blockiert");
      }
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const selectFolder = (nextFolder) => {
    setFolder(nextFolder);
    setSelectedId(null);
  };

  const openMessage = (message) => {
    setSelectedId(message.id);
    if (folder === "inbox" && !message.read) {
      qc.setQueriesData({ queryKey: ["emails"] }, (old = []) => old.map((m) => (
        m.id === message.id ? { ...m, read: true } : m
      )));
    }
  };

  const openRelatedMessage = (message) => {
    setFolder(message.direction === "sent" ? "sent" : "inbox");
    setSelectedId(message.id);
  };

  if (!cfg) {
    return (
      <div className="stellar-page max-w-2xl">
        <div className="stellar-page-heading stellar-heading-indigo">
          <span className="stellar-module-icon stellar-module-indigo"><Mail className="h-5 w-5" /></span>
          <h1 className="font-display text-3xl font-bold tracking-tight">Posteingang</h1>
        </div>
        <div className="mt-6 premium-card p-8 text-center">
          <Mail className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground mb-4">Kein IMAP-Konto konfiguriert.</p>
          <Link to="/einstellungen" className="text-primary hover:underline text-sm" data-testid="email-goto-settings">
            E-Mail-Konto in den Einstellungen einrichten →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="stellar-mail-shell">
      <div className={`${selectedId ? "hidden md:flex" : "flex"} stellar-mail-list w-full md:w-[420px] shrink-0 flex-col`}>
        <div className="stellar-mail-list-header px-3 sm:px-4 py-4 border-b border-border/60">
          <div className="flex items-center gap-3">
            <span className="stellar-module-icon stellar-module-indigo shrink-0">
              {folder === "inbox" ? <Inbox className="h-5 w-5" /> : <Send className="h-5 w-5" />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold text-xl">{folder === "inbox" ? "Posteingang" : "Gesendet"}</div>
              <div className="text-xs text-muted-foreground truncate">
                {folder === "inbox" && unreadCount > 0 ? `${unreadCount} ungelesen · ` : ""}{cfg.user}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <Button className={`shadow-sm ${folder === "sent" ? "col-span-2" : ""}`} onClick={() => { setCompose({ to: "", subject: "", body: "", reply_to_id: null }); setComposeFiles([]); setComposeOpen(true); }} data-testid="email-compose-btn">
              <Send className="h-4 w-4 mr-1.5" /> Neue E-Mail
            </Button>
            {folder === "inbox" ? (
              <Button variant="outline" onClick={() => syncMut.mutate()} disabled={syncMut.isPending} data-testid="email-sync-btn">
                <RefreshCcw className={`h-4 w-4 mr-1.5 ${syncMut.isPending ? "animate-spin" : ""}`} /> Aktualisieren
              </Button>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-1 mt-4 rounded-xl bg-slate-100/90 p-1" data-testid="email-folder-tabs">
            <button
              type="button"
              onClick={() => selectFolder("inbox")}
              className={`inline-flex min-h-10 items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-sm font-semibold transition ${folder === "inbox" ? "bg-white text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              <Inbox className="h-4 w-4" /> Posteingang
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${unreadCount ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-500"}`}>
                {unreadCount || inboxMessages.length}
              </span>
            </button>
            <button
              type="button"
              onClick={() => selectFolder("sent")}
              className={`inline-flex min-h-10 items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-sm font-semibold transition ${folder === "sent" ? "bg-white text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >
              <Send className="h-4 w-4" /> Gesendet <span className="opacity-60 text-xs">({sentMessages.length})</span>
            </button>
          </div>
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Absender, Adresse, Betreff oder Inhalt…"
              className="h-11 bg-white pl-9"
              data-testid="email-search"
            />
          </div>
        </div>
        <div className="stellar-mail-items flex-1 overflow-auto">
          {messages.length === 0 && (
            <div className="flex min-h-64 flex-col items-center justify-center p-8 text-center">
              <span className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                {folder === "inbox" ? <Inbox className="h-6 w-6" /> : <Send className="h-6 w-6" />}
              </span>
              <div className="font-display font-semibold">{folder === "inbox" ? "Keine E-Mails" : "Noch nichts gesendet"}</div>
              <div className="mt-1 text-xs text-muted-foreground">
                {folder === "inbox" ? "Mit „Aktualisieren“ prüfen Sie neue Nachrichten." : "Gesendete Nachrichten erscheinen automatisch hier."}
              </div>
            </div>
          )}
          {messages.map((m) => (
            <button
              key={m.id}
              onClick={() => openMessage(m)}
              data-testid={`email-item-${m.id}`}
              className={`stellar-mail-item w-full text-left p-4 transition-all ${selectedId === m.id ? "is-selected" : ""} ${folder === "inbox" && !m.read ? "is-unread" : ""}`}
            >
              <div className="flex items-start gap-3">
                <span className={`mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                  folder === "sent"
                    ? "bg-emerald-50 text-emerald-600"
                    : (!m.read ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-500")
                }`}>
                  {folder === "sent"
                    ? <Send className="h-4 w-4" />
                    : (m.read ? <MailOpen className="h-4 w-4" /> : <Mail className="h-4 w-4" />)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className={`text-sm truncate flex-1 ${folder === "inbox" && !m.read ? "font-bold" : "font-semibold"}`}>
                      {folder === "sent" ? `An: ${(m.to || []).join(", ")}` : (m.from_name || m.from_email)}
                    </div>
                    <div className="text-[10px] text-muted-foreground shrink-0">{fmt(m.date)}</div>
                  </div>
                  <div className={`mt-0.5 text-sm truncate ${folder === "inbox" && !m.read ? "font-semibold text-foreground" : "text-slate-700"}`}>{m.subject}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2 mt-1 leading-relaxed">{m.snippet}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {folder === "inbox" && !m.read && <span className="stellar-mail-badge is-new">Neu</span>}
                    {folder === "inbox" && repliedIds.has(m.id) && (
                      <span className="stellar-mail-badge is-replied"><CheckCircle2 className="h-3 w-3" /> Beantwortet</span>
                    )}
                    {folder === "inbox" && isContactRequest(m.subject) && (
                      <span className="stellar-mail-badge is-request"><Sparkles className="h-3 w-3" /> Anfrage</span>
                    )}
                    {m.customer_name && (
                      <span className="stellar-mail-badge is-customer"><User className="h-3 w-3" /> {m.customer_name}</span>
                    )}
                    {m.kind === "payment_reminder" && <span className="stellar-mail-badge is-request"><BellRing className="h-3 w-3" /> Zahlungserinnerung</span>}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className={`${selectedId ? "flex" : "hidden md:flex"} stellar-mail-reader flex-1 min-w-0 flex-col`}>
        {!current ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
            <span className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-400">
              <MailOpen className="h-8 w-8" />
            </span>
            <div className="font-display text-lg font-semibold text-slate-700">E-Mail auswählen</div>
            <div className="mt-1 max-w-xs text-sm">Öffnen Sie links eine Nachricht, um sie zu lesen oder zu bearbeiten.</div>
          </div>
        ) : (
          <>
            <div className="stellar-mail-reader-header border-b border-border/60 p-2.5 sm:p-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-4">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-1.5">
                    <span className={`stellar-mail-badge ${folder === "inbox" ? "is-inbox" : "is-sent"}`}>
                      {folder === "inbox" ? <Inbox className="h-3 w-3" /> : <Send className="h-3 w-3" />}
                      {folder === "inbox" ? "Eingegangen" : "Gesendet"}
                    </span>
                    {folder === "inbox" && repliedIds.has(current.id) && (
                      <span className="stellar-mail-badge is-replied"><CheckCircle2 className="h-3 w-3" /> Beantwortet</span>
                    )}
                    {folder === "inbox" && isContactRequest(current.subject) && (
                      <span className="stellar-mail-badge is-request"><Sparkles className="h-3 w-3" /> Kundenanfrage</span>
                    )}
                  </div>
                  <h2 className="mb-1 break-words font-display text-lg font-bold leading-tight sm:text-xl">{current.subject}</h2>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                    {folder === "inbox" ? (
                      <>
                        <span className="font-semibold text-slate-700">{current.from_name || current.from_email}</span>
                        <span className="break-all">&lt;{current.from_email}&gt;</span>
                      </>
                    ) : (
                      <><span>An:</span><span className="break-all font-semibold text-slate-700">{(current.to || []).join(", ")}</span></>
                    )}
                    <span>· {fmt(current.date)}</span>
                    {folder === "inbox" && current.customer_id && (
                      <Link to={`/kunden/${current.customer_id}`} className="inline-flex items-center gap-1 font-semibold text-primary hover:underline" data-testid="email-customer-link">
                        <User className="h-3 w-3" /> {current.customer_name}
                      </Link>
                    )}
                    {folder === "sent" && current.invoice_id && (
                      <Link to={`/rechnungen/${current.invoice_id}`} className="inline-flex items-center gap-1 font-semibold text-violet-700 hover:underline" data-testid="email-invoice-link">
                        <FileText className="h-3 w-3" /> Rechnung
                      </Link>
                    )}
                  </div>
                </div>
                <div className={`stellar-mail-actions grid w-full shrink-0 items-center gap-2 ${folder === "inbox" ? "grid-cols-3" : "grid-cols-2"} md:flex md:w-auto`}>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-10 w-full min-w-0 border-emerald-200 bg-emerald-50 px-2 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800 md:hidden"
                    onClick={() => setSelectedId(null)}
                    title="Zurück zum Posteingang"
                    aria-label="Zurück zum Posteingang"
                    data-testid="email-back-to-list"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  {folder === "inbox" && (
                    <Button
                      size="sm"
                      className="h-10 w-full min-w-0 px-2 md:w-[124px]"
                      onClick={() => openReply(current)}
                      data-testid="email-reply-btn"
                    >
                      <Reply className="h-4 w-4 mr-1.5" /> Antworten
                    </Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-10 w-full min-w-0 border-amber-200 bg-amber-50 px-2 text-amber-800 hover:bg-amber-100 hover:text-amber-900 md:w-[124px]"
                        data-testid="email-more-actions"
                      >
                        <MoreHorizontal className="h-4 w-4 mr-1.5" /> Mehr
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-60">
                      <DropdownMenuItem onSelect={() => copyEmailText(current)} data-testid="email-copy-text">
                        <Copy className="h-4 w-4 mr-2 text-slate-600" /> Text kopieren
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {folder === "inbox" && (
                        <>
                          <DropdownMenuItem onSelect={() => importAsAppointment(current, "arbeitstermin")} data-testid="email-create-appointment">
                            <CalendarPlus className="h-4 w-4 mr-2 text-blue-600" /> Termin erstellen
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => importAsAppointment(current, "begutachtung")} data-testid="email-import-contact-form">
                            <ClipboardCheck className="h-4 w-4 mr-2 text-violet-600" /> Als Begutachtung
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={() => {
                              if (confirm(`Absender ${current.from_email} blockieren und Nachricht als Spam markieren?`)) spamMut.mutate(current.id);
                            }}
                            data-testid="email-spam-btn"
                          >
                            <ShieldBan className="h-4 w-4 mr-2 text-amber-600" /> Als Spam markieren
                          </DropdownMenuItem>
                        </>
                      )}
                      {folder === "sent" && current.reply_to_id && (
                        <DropdownMenuItem onSelect={() => { setFolder("inbox"); setSelectedId(current.reply_to_id); }} data-testid="email-open-original">
                          <ArrowLeft className="h-4 w-4 mr-2" /> Ursprüngliche Nachricht
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onSelect={() => { if (confirm("Diese E-Mail wirklich löschen?")) deleteMut.mutate(current.id); }}
                        data-testid="email-delete-btn"
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Löschen
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
            <div className="stellar-mail-body flex-1 min-w-0 overflow-auto p-2.5 sm:p-4">
              {conversationItems.length > 1 && (
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-slate-500" data-testid="email-conversation">
                  <MessagesSquare className="h-3.5 w-3.5 text-indigo-500" /> Verlauf · {currentAddress}
                  <span className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] text-indigo-700">{conversationItems.length}</span>
                </div>
              )}
              <div className="space-y-3">
                {conversationItems.map((message) => {
                  const isSelected = message.id === selectedId;
                  const isSent = message.direction === "sent";
                  return (
                    <article
                      key={`${message.direction}-${message.id}`}
                      id={`email-thread-${message.id}`}
                      className={`stellar-mail-thread-item ${isSent ? "is-sent" : "is-inbox"} ${isSelected ? "is-selected" : ""}`}
                      data-testid={`email-thread-${message.id}`}
                    >
                      {!isSelected && (
                        <div className="flex flex-wrap items-start gap-2 border-b border-border/50 px-3 py-2.5">
                          <span className={`stellar-mail-badge ${isSent ? "is-sent" : "is-inbox"}`}>
                            {isSent ? <Send className="h-3 w-3" /> : <Inbox className="h-3 w-3" />}
                            {isSent ? "Gesendet" : "Empfangen"}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="break-words text-sm font-semibold text-slate-900">{message.subject}</div>
                            <div className="mt-0.5 break-all text-xs text-muted-foreground">
                              {isSent ? `An: ${(message.to || []).join(", ")}` : `Von: ${message.from_name || message.from_email}`}
                            </div>
                          </div>
                          <time className="text-[11px] text-muted-foreground">{fmt(message.date)}</time>
                        </div>
                      )}
                      {message.text ? (() => {
                        const parts = splitQuotedMail(message.text);
                        return (
                          <div className="stellar-mail-message-content break-words">
                            <div className="whitespace-pre-wrap">
                              {renderTextWithLinks(parts.current)}
                            </div>
                            {parts.quoted && (
                              <details className="stellar-mail-quoted-details">
                                <summary>Vorherige Nachrichten anzeigen</summary>
                                <div className="stellar-mail-quoted whitespace-pre-wrap">
                                  {renderTextWithLinks(parts.quoted)}
                                </div>
                              </details>
                            )}
                          </div>
                        );
                      })() : message.html ? (
                        <div className="bg-white p-2 sm:p-4">
                          <iframe
                            title={`E-Mail ${message.id}`}
                            srcDoc={message.html}
                            sandbox=""
                            className={`block w-full rounded-lg border-0 bg-white ${isSelected ? "min-h-[62dvh] sm:min-h-[560px]" : "min-h-[300px]"}`}
                          />
                        </div>
                      ) : (
                        <div className="px-4 py-4 text-sm text-muted-foreground">Kein Textinhalt</div>
                      )}
                      {message.attachments?.length > 0 && (
                        <div className="flex flex-wrap gap-2 border-t border-border/50 px-4 py-3">
                          {message.attachments.map((att) => (
                            <a
                              key={att.id || att.filename}
                              href={fileUrl(att.storage_path)}
                              target="_blank"
                              rel="noreferrer"
                              download={att.filename}
                              className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-indigo-100 bg-indigo-50/60 px-2.5 py-2 text-xs text-indigo-800 hover:bg-indigo-100"
                            >
                              <Paperclip className="h-3.5 w-3.5 shrink-0" />
                              <span className="max-w-[220px] truncate">{att.filename}</span>
                              <span className="shrink-0 text-indigo-500">({Math.round((att.size || 0) / 1024)} KB)</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>

      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="stellar-compose-dialog w-[calc(100vw-1rem)] max-h-[92dvh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                {compose.reply_to_id ? <Reply className="h-4 w-4" /> : <Send className="h-4 w-4" />}
              </span>
              {compose.reply_to_id ? "Antwort schreiben" : "Neue E-Mail"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="mb-1.5 block font-semibold">Empfänger</Label><Input value={compose.to} onChange={(e) => setCompose({...compose, to: e.target.value})} placeholder="empfaenger@example.com" data-testid="compose-to" /></div>
            <div><Label className="mb-1.5 block font-semibold">Betreff</Label><Input value={compose.subject} onChange={(e) => setCompose({...compose, subject: e.target.value})} data-testid="compose-subject" /></div>
            <div><Label className="mb-1.5 block font-semibold">Nachricht</Label><Textarea rows={12} value={compose.body} onChange={(e) => setCompose({...compose, body: e.target.value})} data-testid="compose-body" /></div>
            <div>
              <input
                ref={attachmentInputRef}
                type="file"
                multiple
                className="hidden"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                onChange={(event) => {
                  addComposeFiles(event.target.files);
                  event.target.value = "";
                }}
                data-testid="compose-attachment-input"
              />
              <Button type="button" variant="outline" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50" onClick={() => attachmentInputRef.current?.click()} data-testid="compose-add-attachment">
                <Paperclip className="h-4 w-4 mr-1.5" /> Datei anhängen
              </Button>
              <span className="ml-2 text-xs text-muted-foreground">max. 20 MB gesamt</span>
              {composeFiles.length > 0 && (
                <div className="mt-3 grid gap-2">
                  {composeFiles.map((file, index) => (
                    <div key={`${file.name}-${file.size}-${index}`} className="flex min-w-0 items-center gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2">
                      <Paperclip className="h-4 w-4 shrink-0 text-indigo-500" />
                      <span className="min-w-0 flex-1 truncate text-sm">{file.name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">{file.size < 1024 * 1024 ? `${Math.ceil(file.size / 1024)} KB` : `${(file.size / 1024 / 1024).toFixed(1)} MB`}</span>
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-destructive" onClick={() => setComposeFiles((files) => files.filter((_, fileIndex) => fileIndex !== index))} title="Anhang entfernen">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" className="min-h-11" onClick={() => setComposeOpen(false)}>Abbrechen</Button>
            <Button className="min-h-11 min-w-36" onClick={() => sendMut.mutate()} disabled={!compose.to || !compose.subject || sendMut.isPending} data-testid="compose-send">
              <Send className="h-4 w-4 mr-1" /> {sendMut.isPending ? "Senden…" : "Senden"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
